import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import * as d3 from "d3";
import { allNodes } from "../data/nodes";
import { allEdges } from "../data/edges";
import { categoryColors } from "../data/categoryColors";
import { Search, MousePointerClick, Hand, Plus, Minus, Eye, Palette, MousePointer, ChevronRight, Home, Layers, X, ChevronLeft } from 'lucide-react';

export default function AIKnowledgeBase() {
  // Add this helper function at the top of the component
  const isMobileView = () => {
    return window.innerWidth < 768;
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedTerm, setExpandedTerm] = useState(null);
  const [edgeTooltip, setEdgeTooltip] = useState(null);
  const [nodeTooltip, setNodeTooltip] = useState(null);
  const [graphInitialized, setGraphInitialized] = useState(false);
  const [viewMode, setViewMode] = useState("global");
  const [currentZoomScale, setCurrentZoomScale] = useState(1);
  const [overlayDismissed, setOverlayDismissed] = useState(false);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [zoomInProgress, setZoomInProgress] = useState(false);
  const [forceRender, setForceRender] = useState(0);
  const [helpVisible, setHelpVisible] = useState(false);
  const [showAllNodes, setShowAllNodes] = useState(false);
  const [navigationStack, setNavigationStack] = useState([1]);
  const [currentRootId, setCurrentRootId] = useState(1);
  const [showMobileCategories, setShowMobileCategories] = useState(false);
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false);
  const [showMobilePanel, setShowMobilePanel] = useState(false);
  const [focusGraphNode, setFocusGraphNode] = useState(null);

  const svgRef = useRef(null);
  const graphContainerRef = useRef(null);
  const termListRef = useRef(null);
  const termItemRefs = useRef({});
  const simulationRef = useRef(null);
  const zoomBehaviorRef = useRef(null);
  const d3Ref = useRef(null);

  // Collapsible breadcrumb (desktop)
  const [breadcrumbCollapsed, setBreadcrumbCollapsed] = useState(false);

  const clearAllCustomRadii = () => {
    allNodes.forEach(node => {
      delete node.customRadius;
    });
  };

  const getSafeBounds = (width, height) => {
    const isMobile = isMobileView();
    // Whether a breadcrumb-like bar is visible
    const breadcrumbVisible =
      (!showAllNodes && navigationStack.length > 1) ||
      (viewMode === 'focus' && !!selectedNode);
    // Approximate heights (you can tweak these if you style the bar differently)
    const breadcrumbHeight = isMobile ? 48 : (breadcrumbCollapsed ? 14 : 56);
    const topPad = breadcrumbVisible ? breadcrumbHeight + 8 : 8;
    // Bottom panel sizing (you already vary by isPanelExpanded & device)
    const bottomPanelVisible = !!selectedNode;
    const panelHeight = bottomPanelVisible
      ? (isPanelExpanded
        ? (isMobile ? height * 0.65 : height * 0.5)
        : (isMobile ? 200 : Math.max(180, height * 0.25)))
      : 0;
    const bottomPad = bottomPanelVisible ? Math.min(panelHeight + 8, height * 0.65) : 8;
    const leftPad = 8;
    const rightPad = 8;
    return { x0: leftPad, y0: topPad, x1: width - rightPad, y1: height - bottomPad };
  };

  useEffect(() => {
    if (d3Ref.current) return;
    d3Ref.current = d3;
  }, []);

  useEffect(() => {
    setShowMobileCategories(false);
    setShowMobileSuggestions(false);
  }, [navigationStack, viewMode, selectedNode]);

  const getVisibleNodes = () => {
    if (showAllNodes) {
      return allNodes;
    }
    const rootNode = allNodes.find(n => n.id === currentRootId);
    if (!rootNode) return [allNodes[0]];
    const childEdges = allEdges.filter(e => e.from === currentRootId);
    const childIds = childEdges.map(e => e.to);
    const childNodes = allNodes.filter(n => childIds.includes(n.id));
    return [rootNode, ...childNodes];
  };

  const getVisibleEdges = () => {
    const visibleNodes = getVisibleNodes();
    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));
    return allEdges.filter(e =>
      visibleNodeIds.has(e.from) && visibleNodeIds.has(e.to)
    );
  };

  const uniqueCategories = Array.from(new Set(allNodes.map((node) => node.category))).sort();

  const filteredNodes = searchTerm
    ? allNodes.filter(
      (node) =>
        node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.definition.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : getVisibleNodes();

  const groupedNodes = uniqueCategories
    .map((category) => {
      const nodesInCategory = filteredNodes.filter((node) => node.category === category);
      return {
        category,
        nodes: nodesInCategory.sort((a, b) => a.label.localeCompare(b.label)),
      };
    })
    .filter((group) => group.nodes.length > 0);

  const getConnectedNodes = (nodeId) => {
    if (!nodeId) return [];
    const connectedEdges = allEdges.filter((edge) =>
      (edge.from === nodeId || edge.to === nodeId)
    );
    const connectedNodeIds = new Set();
    connectedEdges.forEach((edge) => {
      if (edge.from === nodeId) connectedNodeIds.add(edge.to);
      if (edge.to === nodeId) connectedNodeIds.add(edge.from);
    });
    return Array.from(connectedNodeIds)
      .map((id) => allNodes.find((node) => node.id === id))
      .filter((node) => node !== undefined);
  };

  const getSubgraph = (nodeId) => {
    if (!nodeId) return { nodes: [], edges: [] };
    const selectedNodeObj = allNodes.find((node) => node.id === nodeId);
    if (!selectedNodeObj) return { nodes: [], edges: [] };
    const connectedNodes = getConnectedNodes(nodeId);
    const subgraphNodes = [selectedNodeObj, ...connectedNodes];
    const subgraphEdges = allEdges.filter(
      (edge) =>
        (edge.from === nodeId || edge.to === nodeId) &&
        subgraphNodes.some((n) => n.id === edge.from) &&
        subgraphNodes.some((n) => n.id === edge.to)
    );
    return { nodes: subgraphNodes, edges: subgraphEdges };
  };

  const getNodeColor = (node) => {
    return categoryColors[node.category] || "#444444";
  };

  const getNodeRadius = (node, isSelected = false) => {
    const baseSizeMultiplier = node.category === "Core Concept" || node.category === "Root" ? 2.0 : 1;
    const selectedMultiplier = isSelected ? 1.5 : 1;
    const connectionCount = allEdges.filter((e) => e.from === node.id || e.to === node.id).length;
    let baseSize;
    if (viewMode === "focus") {
      baseSize = Math.max(15, Math.min(35, 15 + connectionCount));
    } else {
      const isMobile = isMobileView();
      if (isMobile){
        baseSize = Math.max(12, Math.min(28, 10 + connectionCount*0.5));
      }else{
      baseSize = Math.max(16, Math.min(32, 16 + connectionCount * 0.5));
      }
    }
    // IMPROVED: Better label length accommodation
    const labelLength = node.label.length;
    // More aggressive scaling for longer labels
    let labelFactor;
    if (labelLength > 25) {
      labelFactor = 2.2; // Very long labels
    } else if (labelLength > 20) {
      labelFactor = 1.9; // Long labels
    } else if (labelLength > 15) {
      labelFactor = 1.6; // Medium labels
    } else if (labelLength > 10) {
      labelFactor = 1.3; // Slightly longer
    } else {
      labelFactor = 1.0; // Short labels
    }
    // Increase minimum and maximum sizes
    const radius = baseSize * baseSizeMultiplier * selectedMultiplier * labelFactor;
    // Higher minimums to ensure readability
    const minRadius = viewMode === "focus" ? 35 : 38; // Increased from 25/20
    const maxRadius = viewMode === "focus" ? 80 : 70; // Increased from 60/45
    return Math.max(minRadius, Math.min(maxRadius, radius));
  };

  const getEffectiveRadius = (node, isSelected = false) => {
    return node.customRadius || getNodeRadius(node, isSelected);
  };

  const wrapNodeLabel = (text, maxWidth) => {
    if (!text || text.length <= 3) return [text];
    if (text.length <= 10) return [text]; // Increased from 8
    const words = text.split(/\s+/);
    const lines = [];
    let currentLine = words[0];
    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const testLine = currentLine + " " + word;
      if (testLine.length > maxWidth) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine);
    // Allow up to 3 lines instead of 2
    if (lines.length > 3) {
      return [...lines.slice(0, 2), "..."];
    }
    return lines;
  };

  // ---------------------------------------------------------------
  // BOUNDARY FORCE – keeps nodes inside the SVG rectangle
  // ---------------------------------------------------------------
  function forceBoundary(getRect, getRadius, padding = 0) {
    let nodes = [];
    function force() {
      const { x0, y0, x1, y1 } = getRect();
      for (const n of nodes) {
        const r = getRadius(n) + padding;
        n.x = Math.max(x0 + r, Math.min(x1 - r, n.x));
        n.y = Math.max(y0 + r, Math.min(y1 - r, n.y));
      }
    }
    force.initialize = function (_nodes) { nodes = _nodes; };
    return force;
  }

  const hasChildren = (nodeId) => {
    return allEdges.some(e => e.from === nodeId);
  };

  // const getFullPathToNode = (targetId) => {
  //   if (targetId === 1) return [1];
  //   const path = [];
  //   let currentId = targetId;
  //   while (currentId && currentId !== 1) {
  //     path.unshift(currentId);
  //     const parentEdge = allEdges.find(e => e.to === currentId);
  //     if (!parentEdge) break;
  //     currentId = parentEdge.from;
  //   }
  //   if (currentId === 1) {
  //     path.unshift(1);
  //   }
  //   return path;
  // };
  const getFullPathToNode = (targetId) => {
    if (targetId === 1) return [1];
    const path = [];
    let currentId = targetId;

    while (currentId && currentId !== 1) {
      path.unshift(currentId);
      const parentEdge = allEdges.find(e => e.to === currentId);
      if (!parentEdge) break;
      currentId = parentEdge.from;
    }

    if (currentId === 1) {
      path.unshift(1);
    }

    return path;
  };
  const handleNodeClick = (node) => {
    if (searchTerm) {
      handleNodeSelect(node);
      return;
    }
    if (hasChildren(node.id) && !showAllNodes) {
      // Node has children - navigate in hierarchy view
      setSelectedNode(node);
      setExpandedTerm(node.id);
      // Check if this node already exists in the navigation stack
      const nodeIndex = navigationStack.indexOf(node.id);
      if (nodeIndex !== -1) {
        // Node exists in stack - navigate back to it (trim the stack)
        const newStack = navigationStack.slice(0, nodeIndex + 1);
        setNavigationStack(newStack);
        setCurrentRootId(node.id);
      } else {
        // New node - add it to the current path
        setNavigationStack([...navigationStack, node.id]);
        setCurrentRootId(node.id);
      }
      setEdgeTooltip(null);
      setNodeTooltip(null);
      setForceRender((prev) => prev + 1);
    } else {
      // Leaf node - add to current path and go to focus view
      const nodeIndex = navigationStack.indexOf(node.id);
      if (nodeIndex === -1) {
        // Node not in stack - add it
        setNavigationStack([...navigationStack, node.id]);
      }
      // If already in stack, keep it as is
      handleNodeSelect(node);
    }
  };

  const handleNodeSelect = (node) => {
    setViewMode("focus");
    setSelectedNode(node);
    setFocusGraphNode(node);
    setExpandedTerm(node.id);
    setIsPanelExpanded(searchTerm ? true : false);
    setEdgeTooltip(null);
    setNodeTooltip(null);

    // === FIXED: Always update breadcrumb path, even when searching ===
    const fullPath = getFullPathToNode(node.id);
    if (fullPath.length > 0) {
      setNavigationStack(fullPath);
    } else {
      // Fallback: if no path (shouldn't happen), at least show the node
      setNavigationStack([node.id]);
    }

    // Optional: Clear search input after selection for cleaner UX
    if (searchTerm) {
      setTimeout(() => setSearchTerm(""), 300);
    }

    // Trigger graph re-render
    setTimeout(() => {
      setForceRender((prev) => prev + 1);
    }, 10);

    // Scroll to the term in the sidebar list
    setTimeout(() => {
      const termElement = termItemRefs.current[node.id];
      if (termElement) {
        termElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  };
  const navigateBack = () => {
    if (navigationStack.length > 1) {
      const newStack = navigationStack.slice(0, -1);
      setNavigationStack(newStack);
      setCurrentRootId(newStack[newStack.length - 1]);
      setEdgeTooltip(null);
      setNodeTooltip(null);
      setSelectedNode(null);
      setExpandedTerm(null);
      setViewMode("global")
      setFocusGraphNode(null);
      clearAllCustomRadii();
      // Clear any pinned positions & gently reset around center
      const container = graphContainerRef.current;
      const width = container?.clientWidth ?? 800;
      const height = container?.clientHeight ?? 600;
      const visibleNodes = getVisibleNodes();
      visibleNodes.forEach((node) => {
        node.fx = null; node.fy = null;
        node.x = width / 2 + (Math.random() - 0.5) * 100;
        node.y = height / 2 + (Math.random() - 0.5) * 100;
        delete node.customRadius; // Clear custom radius
      });
      // Reset zoom to 1x to avoid inherited transforms
      if (svgRef.current && zoomBehaviorRef.current && d3Ref.current) {
        const d3 = d3Ref.current;
        d3.select(svgRef.current).call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
        setCurrentZoomScale(1);
      }
      setTimeout(() => {
        setForceRender((prev) => prev + 1);
      }, 50);
    }
  };

  const navigateToNode = (nodeId) => {
    const index = navigationStack.indexOf(nodeId);
    if (index !== -1) {
      const newStack = navigationStack.slice(0, index + 1);
      setNavigationStack(newStack);
      setCurrentRootId(nodeId);
      setEdgeTooltip(null);
      setNodeTooltip(null);
      setSelectedNode(null);
      setExpandedTerm(null);
      setViewMode("global");
      clearAllCustomRadii();
      // Clear any pinned positions & gently reset around center
      const container = graphContainerRef.current;
      const width = container?.clientWidth ?? 800;
      const height = container?.clientHeight ?? 600;
      const visibleNodes = getVisibleNodes();
      visibleNodes.forEach((node) => {
        node.fx = null; node.fy = null;
        node.x = width / 2 + (Math.random() - 0.5) * 100;
        node.y = height / 2 + (Math.random() - 0.5) * 100;
        delete node.customRadius; // Clear custom radius
      });
      // Reset zoom to 1x
      if (svgRef.current && zoomBehaviorRef.current && d3Ref.current) {
        const d3 = d3Ref.current;
        d3.select(svgRef.current).call(zoomBehaviorRef.current.transform, d3.zoomIdentity);
        setCurrentZoomScale(1);
      }
      setTimeout(() => {
        setForceRender((prev) => prev + 1);
      }, 50);
    }
  };

  const toggleShowAll = () => {
    const wasShowingAll = showAllNodes;
    setShowAllNodes(!showAllNodes);
    setEdgeTooltip(null);
    setNodeTooltip(null);
    setSelectedNode(null);
    setExpandedTerm(null);
    clearAllCustomRadii();
    setViewMode("global");
    setFocusGraphNode(null);
    setNavigationStack([1]);
    setCurrentRootId(1);
    setTimeout(() => {
      setForceRender((prev) => prev + 1);
    }, 50);

  };

  const resetToRoot = () => {
    setNavigationStack([1]);
    setCurrentRootId(1);
    setShowAllNodes(false);
    setEdgeTooltip(null);
    setNodeTooltip(null);
    setSelectedNode(null);
    setExpandedTerm(null);
    clearAllCustomRadii();
    setViewMode("global");
    setTimeout(() => {
      setForceRender((prev) => prev + 1);
    }, 50);
  };

  const toggleViewMode = () => {
    const newMode = viewMode === "focus" ? "global" : "focus";
    setViewMode(newMode);
    if (newMode === "global") {
      setSelectedNode(null);
      setExpandedTerm(null);
    }
    setEdgeTooltip(null);
    setNodeTooltip(null);
  };

  const focusOnNode = (d3, nodeId) => {
    if (!svgRef.current || !zoomBehaviorRef.current) return;
    const svg = d3.select(svgRef.current);
    const node = allNodes.find((n) => n.id === nodeId);
    if (node && node.x && node.y) {
      const scale = 1.5;
      const width = graphContainerRef.current?.clientWidth || 800;
      const height = graphContainerRef.current?.clientHeight || 600;
      const translateX = width / 2 - scale * node.x;
      const translateY = height / 2 - scale * node.y;
      svg
        .transition()
        .duration(750)
        .call(zoomBehaviorRef.current.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale));
      setCurrentZoomScale(scale);
    }
  };

  const handleZoomIn = useCallback(() => {
    if (zoomInProgress || !svgRef.current || !zoomBehaviorRef.current || !d3Ref.current) return;
    setZoomInProgress(true);
    const d3 = d3Ref.current;
    const svg = d3.select(svgRef.current);
    svg
      .transition()
      .duration(300)
      .call(zoomBehaviorRef.current.scaleBy, 1.3)
      .on("end", () => {
        const transform = d3.zoomTransform(svg.node());
        setCurrentZoomScale(transform.k);
        setZoomInProgress(false);
      });
  }, [zoomInProgress]);

  const handleZoomOut = useCallback(() => {
    if (zoomInProgress || !svgRef.current || !zoomBehaviorRef.current || !d3Ref.current) return;
    setZoomInProgress(true);
    const d3 = d3Ref.current;
    const svg = d3.select(svgRef.current);
    svg
      .transition()
      .duration(300)
      .call(zoomBehaviorRef.current.scaleBy, 0.7)
      .on("end", () => {
        const transform = d3.zoomTransform(svg.node());
        setCurrentZoomScale(transform.k);
        setZoomInProgress(false);
      });
  }, [zoomInProgress]);

  useEffect(() => {
    if (!graphInitialized && svgRef.current && d3Ref.current) {
      setGraphInitialized(true);
      setViewMode("global");
      setSelectedNode(null);
      setTimeout(() => {
        setForceRender((prev) => prev + 1);
      }, 100);
    }
  }, [graphInitialized]);

  useEffect(() => {
    if (!svgRef.current || !d3Ref.current) return;
    // Stop any previous simulation before rebuilding
    if (simulationRef.current) {
      try { simulationRef.current.stop(); } catch (e) { }
      simulationRef.current = null;
    }
    const d3 = d3Ref.current;
    const svg = d3.select(svgRef.current);
    // svg.on("click",null)
    const container = graphContainerRef.current;
    const width = container?.clientWidth ?? 800;
    // Base height from container; focus view later reduces visible area via panel,
    // but safe bounds handles that when we pass width/height into render functions.

    let height = container?.clientHeight ?? 600;
    // Clear
    svg.selectAll("*").remove();
    const g = svg.append("g");
    // Zoom
    const zoom = d3
      .zoom()
      .scaleExtent([0.1, 5])
      .on("zoom", (event) => {
        g.attr("transform", event.transform);
        setCurrentZoomScale(event.transform.k);
      });
    svg.call(zoom);
    zoomBehaviorRef.current = zoom;
    // if (viewMode === "focus" && selectedNode) {
    //   renderFocusView(d3, svg, g, zoom, width, height);
    // } else {
    //   renderGlobalView(d3, svg, g, zoom, width, height);
    // }

    if (viewMode === "global") {
      renderGlobalView(d3, svg, g, zoom, width, height);
    } else if (viewMode === "focus" && focusGraphNode) {
      renderFocusView(d3, svg, g, zoom, width, height, focusGraphNode);
    }
    // Clear customRadius after render to avoid persistence
    allNodes.forEach(node => delete node.customRadius);
  }, [viewMode, selectedNode, focusGraphNode, forceRender, overlayDismissed, showAllNodes, currentRootId, breadcrumbCollapsed, isPanelExpanded]);

  const renderFocusView = (d3, svg, g, zoom, width, height, focusGraphNode) => {
    getVisibleNodes().forEach(node => delete node.customRadius);
    if (!focusGraphNode) return;
    // if (!selectedNode) return;
    const { nodes: subgraphNodes, edges: subgraphEdges } = getSubgraph(focusGraphNode.id);
    const edgesData = subgraphEdges
      .map((e) => ({
        source: subgraphNodes.find((n) => n.id === e.from),
        target: subgraphNodes.find((n) => n.id === e.to),
        label: e.label,
      }))
      .filter((e) => e.source && e.target);
    const isMobile = isMobileView();
    const safe = getSafeBounds(width, height);
    const safeCx = (safe.x0 + safe.x1) / 2;
    const safeCy = (safe.y0 + safe.y1) / 2;

    // Set uniform radii for connected nodes, parent larger
    const connectedNodes = subgraphNodes.filter((n) => n.id !== focusGraphNode.id);
    let maxConnRadius = 0;
    connectedNodes.forEach(node => {
      maxConnRadius = Math.max(maxConnRadius, getNodeRadius(node, false));
    });
    connectedNodes.forEach(node => {
      node.customRadius = maxConnRadius;
    });
    const selectedNodeObj = subgraphNodes.find((n) => n.id === focusGraphNode.id);
    selectedNodeObj.customRadius = Math.max(getNodeRadius(selectedNodeObj, true), maxConnRadius * 1.2);

    // Initial positioning: center selected inside safe area; arrange neighbors on a circle within safe bounds
    selectedNodeObj.x = safeCx;
    selectedNodeObj.y = safeCy;
    const maxR = Math.min((safe.x1 - safe.x0), (safe.y1 - safe.y0)) / 3;
    const radius = Math.max(120, Math.min(300, maxR)); // keep a reasonable circle
    connectedNodes.forEach((node, index) => {
      const angle = (index / Math.max(1, connectedNodes.length)) * 2 * Math.PI;
      node.x = selectedNodeObj.x + Math.cos(angle) * radius;
      node.y = selectedNodeObj.y + Math.sin(angle) * radius;
      // Clamp to ensure within safe bounds
      const pad = node.customRadius + 24;
      node.x = Math.max(safe.x0 + pad, Math.min(safe.x1 - pad, node.x));
      node.y = Math.max(safe.y0 + pad, Math.min(safe.y1 - pad, node.y));
    });
    // Force: link/charge/collision/boundary
    const simulation = d3
      .forceSimulation(subgraphNodes)
      .force(
        "link",
        d3.forceLink(edgesData)
          .id((d) => d.id)
          .distance(isMobile ? 200 : 260)
          .strength(0.5)
      )
      .force("center", d3.forceCenter(safeCx, safeCy))
      .force("charge", d3.forceManyBody().strength(isMobile ? -500 : -100))
      .force(
        "collision",
        d3.forceCollide()
          .radius(d => getEffectiveRadius(d) + (isMobile ? 30 : 50))
          .strength(1.0)
          .iterations(5)
      )
      .force(
        "boundary",
        forceBoundary(
          () => ({ x0: safe.x0, y0: safe.y0, x1: safe.x1, y1: safe.y1 }),
          d => getEffectiveRadius(d),
          24 // margin from edges of safe area
        )
      )
      .velocityDecay(0.6);
    simulationRef.current = simulation;
    // Arrowheads
    const defs = svg.append("defs");
    defs.append("marker")
      .attr("id", "arrow-focus")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 8)
      .attr("refY", 0)
      .attr("markerWidth", 8)
      .attr("markerHeight", 8)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#aaaaaa");
    // Links & textPaths
    const linkGroup = g.append("g").attr("class", "links");
    const link = linkGroup
      .selectAll(".link")
      .data(edgesData)
      .enter()
      .append("path")
      .attr("class", "link")
      .attr("stroke", "#aaaaaa")
      .attr("stroke-width", 2)
      .attr("stroke-opacity", 0.8)
      .attr("fill", "none")
      .attr("marker-end", "url(#arrow-focus)");
    const linkText = linkGroup
      .selectAll(".link-label")
      .data(edgesData)
      .enter()
      .append("text")
      .attr("class", "link-label")
      .attr("font-size", "13px")
      .attr("fill", "#ddd")
      .attr("text-anchor", "middle")
      .attr("dy", -5)
      .attr("font-weight", "500")
      .append("textPath")
      .attr("startOffset", "50%")
      .attr("href", (d, i) => `#edgepath${i}`)
      .text((d) => d.label);
    const edgePaths = linkGroup
      .selectAll(".edgepath")
      .data(edgesData)
      .enter()
      .append("path")
      .attr("class", "edgepath")
      .attr("id", (d, i) => `edgepath${i}`)
      .attr("stroke-width", 20)
      .attr("stroke", "transparent")
      .attr("fill", "none");
    // Nodes
    const nodeGroup = g
      .append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(subgraphNodes)
      .enter()
      .append("g")
      .attr("class", "node-group")
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        handleNodeSelect(d);
        event.stopPropagation();
      })
    
      .on("mouseenter", (event, d) => {
        const nodeElement = event.currentTarget;
        const rect = nodeElement.getBoundingClientRect();
        const svgRect = svgRef.current?.getBoundingClientRect();
        if (svgRect) {
          setNodeTooltip({
            label: d.label,
            category: d.category,               // Always show category
            hasChildren: hasChildren(d.id),     // Show expand hint if has children
            x: rect.left + rect.width / 2 - svgRect.left,
            y: rect.top - svgRect.top - 10,
          });
        }
      })
      .on("mouseleave", () => {
        setEdgeTooltip(null);
        setNodeTooltip(null);
      })
      .call(
        d3.drag()
          .on("start", dragstarted)
          .on("drag", dragged)
          .on("end", dragended)
      );
    nodeGroup
      .append("circle")
      .attr("r", (d) => getEffectiveRadius(d, d.id === focusGraphNode.id))
      .attr("fill", (d) => (d.id === focusGraphNode.id ? "#ff9900" : getNodeColor(d)))
      .attr("stroke", (d) => (d.id === focusGraphNode.id ? "#ffffff" : "#333333"))
      .attr("stroke-width", (d) => (d.id === focusGraphNode.id ? 3 : 1));
    const textGroups = nodeGroup
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", (d) => {
        const radius = getEffectiveRadius(d, d.id === focusGraphNode.id);
        const lineCount = wrapNodeLabel(d.label, radius / 2).length;
        return -((lineCount - 1) * 10) / 2;
      })
      .attr("class", "node-text")
      .attr("data-scale", 1)
      .style("pointer-events", "none")
      .style("user-select", "none");
    // textGroups.each(function (d) {
    //   const text = d3.select(this);
    //   const radius = getEffectiveRadius(d, d.id === selectedNode.id);
    //   const maxCharsPerLine = Math.max(8, Math.floor(radius / 3));
    //   const lines = wrapNodeLabel(d.label, maxCharsPerLine);
    //   const fontSize = Math.min(radius / 2.8, isMobile ? 14 : 16);
    //   text.attr("font-size", `${fontSize}px`);
    //   text.attr("fill", "#ffffff");
    //   text.attr("font-weight", "500");
    //   lines.forEach((line, i) => {
    //     text.append("tspan").attr("x", 0).attr("y", 0).attr("dy", `${i * 1.2}em`).text(line);
    //   });
    textGroups.each(function (d) {
      const text = d3.select(this);
      const radius = getEffectiveRadius(d, d.id === focusGraphNode.id);
      const maxCharsPerLine = Math.max(8, Math.floor(radius / 3.5));
      const labelToDisplay = d.shortLabel || d.label;
      const lines = wrapNodeLabel(labelToDisplay, maxCharsPerLine);
      const fontSize = Math.min(radius / 3.0, isMobile ? 14 : 16);
      text.attr("font-size", `${fontSize}px`);
      // Use the SAME colour as the circle (with fallback)
      text.attr("fill", d.id === focusGraphNode.id ? "#ffffff" : "#ffffff");
      text.attr("font-weight", "500");
      lines.forEach((line, i) => {
        text.append("tspan")
          .attr("x", 0)
          .attr("y", 0)
          .attr("dy", `${i * 1.2}em`)
          .text(line);
      });
    });
    simulation.on("tick", () => {
      link.attr("d", (d) => {
        const sx = d.source.x;
        const sy = d.source.y;
        const tx = d.target.x;
        const ty = d.target.y;
        const dx = tx - sx;
        const dy = ty - sy;
        const dr = Math.sqrt(dx * dx + dy * dy);
        const targetRadius = getEffectiveRadius(d.target);
        const offset = targetRadius + 6;
        const normX = dx / dr;
        const normY = dy / dr;
        const ex = tx - normX * offset;
        const ey = ty - normY * offset;
        return `M ${sx} ${sy} L ${ex} ${ey}`;
      });
      edgePaths.attr("d", (d) => {
        const sx = d.source.x;
        const sy = d.source.y;
        const tx = d.target.x;
        const ty = d.target.y;
        const dx = tx - sx;
        const dy = ty - sy;
        const dr = Math.sqrt(dx * dx + dy * dy);
        const targetRadius = getEffectiveRadius(d.target);
        const offset = targetRadius + 6;
        const normX = dx / dr;
        const normY = dy / dr;
        const ex = tx - normX * offset;
        const ey = ty - normY * offset;
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        if (angle > 90 || angle < -90) {
          return `M${ex},${ey}L${sx},${sy}`;
        } else {
          return `M${sx},${sy}L${ex},${ey}`;
        }
      });
      nodeGroup.attr("transform", (d) => `translate(${d.x}, ${d.y})`);
      if (currentZoomScale !== 1) {
        adjustTextScaling(d3, currentZoomScale);
      }
    });
    // Drag with clamping to safe bounds
    function dragstarted(event) {
      if (!event.active) simulation.alphaTarget(0.1).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
      d3.select(event.sourceEvent.target.parentNode).style("cursor", "grabbing");
    }
    function dragged(event) {
      const pad = getEffectiveRadius(event.subject) + 24;
      const x = Math.max(safe.x0 + pad, Math.min(safe.x1 - pad, event.x));
      const y = Math.max(safe.y0 + pad, Math.min(safe.y1 - pad, event.y));
      event.subject.fx = x;
      event.subject.fy = y;
    }
    function dragended(event) {
      if (!event.active) simulation.alphaTarget(0);
      const node = d3.select(event.sourceEvent.target.parentNode);
      const d = event.subject;
      const cursor = hasChildren(d.id) && !showAllNodes ? "pointer" : "grab";
      node.style("cursor", cursor);
    }
    // Fit content into the safe viewport
    setTimeout(() => {
      const bounds = g.node().getBBox();
      const fullWidth = safe.x1 - safe.x0;
      const fullHeight = safe.y1 - safe.y0;
      const calculatedScale = 0.85 / Math.max(bounds.width / fullWidth, bounds.height / fullHeight);
      const nodeCount = subgraphNodes.length;
      // let maxScale = isMobile ? 1.1 : 1.4;
      // if (nodeCount <= 2) maxScale = isMobile ? 0.6 : 0.9;
      // else if (nodeCount <= 4) maxScale = isMobile ? 0.7 : 1.0;
      // else if (nodeCount <= 6) maxScale = isMobile ? 0.8 : 1.2;
      let maxScale;
      if (nodeCount <= 2) {
        maxScale = isMobile ? 0.8 : 0.8;
      } else if (nodeCount <= 4) {
        maxScale = isMobile ? 0.9 : 0.95;
      } else if (nodeCount <= 6) {
        maxScale = isMobile ? 1.0 : 1.05;
      } else if (nodeCount <= 8) {
        maxScale = isMobile ? 1.1 : 1.15;
      } else {
        maxScale = isMobile ? 1.2 : 1.2;
      }
      const scale = Math.min(calculatedScale, maxScale);
      const translateX = (safe.x0 + fullWidth / 2) - scale * (bounds.x + bounds.width / 2);
      const translateY = (safe.y0 + fullHeight / 2) - scale * (bounds.y + bounds.height / 2);
      svg.call(zoom.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale));
      setCurrentZoomScale(scale);
    }, 120);
    svg.on("click", (event) => {
      const targetElement = event.target;
      // If the click was not on a node
      if (!targetElement.closest(".node-group")) {
        setSelectedNode(null);
        setExpandedTerm(null);
        // setViewMode("global"); 
      }
    });
  };

  const renderGlobalView = (d3, svg, g, zoom, width, height) => {
    getVisibleNodes().forEach(node => delete node.customRadius);
    const visibleNodes = getVisibleNodes();
    const visibleEdges = getVisibleEdges();
    const edgesData = visibleEdges
      .map((e) => ({
        source: visibleNodes.find((n) => n.id === e.from),
        target: visibleNodes.find((n) => n.id === e.to),
        label: e.label,
      }))
      .filter((e) => e.source && e.target);
    const isMobile = isMobileView();
    // const isHierarchical = !showAllNodes && visibleNodes.length <= 10;
    const isHierarchical = !showAllNodes;

    const nodeCount = visibleNodes.length;
    const safe = getSafeBounds(width, height);
    const safeCx = (safe.x0 + safe.x1) / 2;
    const safeCy = (safe.y0 + safe.y1) / 2;

    // Category-based positioning for "Show All" mode
    const categoryGroups = {
      "Root": { x: safeCx, y: safeCy },
      "Core Concept": { x: safeCx, y: safe.y0 + (safe.y1 - safe.y0) * 0.25 },
      "Methodology": { x: safe.x0 + (safe.x1 - safe.x0) * 0.25, y: safeCy - (safe.y1 - safe.y0) * 0.1 },
      "Model": { x: safe.x0 + (safe.x1 - safe.x0) * 0.75, y: safeCy - (safe.y1 - safe.y0) * 0.15 },
      "Tool": { x: safe.x0 + (safe.x1 - safe.x0) * 0.25, y: safeCy + (safe.y1 - safe.y0) * 0.2 },
      "Use Case": { x: safe.x0 + (safe.x1 - safe.x0) * 0.75, y: safeCy + (safe.y1 - safe.y0) * 0.25 },
      "Challenge": { x: safeCx, y: safe.y0 + (safe.y1 - safe.y0) * 0.9 },
    };

    // If hierarchical, set uniform radii for children, parent larger
    if (isHierarchical && visibleNodes.length > 1) {
      const children = visibleNodes.slice(1);
      let maxChildRadius = 0;
      children.forEach(node => {
        maxChildRadius = Math.max(maxChildRadius, getNodeRadius(node, false));
      });
      children.forEach(node => {
        node.customRadius = maxChildRadius;
      });
      const root = visibleNodes[0];
      root.customRadius = Math.max(getNodeRadius(root, selectedNode?.id === root.id), maxChildRadius * 1.2);
    }

    // Initialize node positions with good distribution
    if (isHierarchical && visibleNodes.length > 1) {
      // STATIC hierarchical layout
      const rootNode = visibleNodes[0];
      const childNodes = visibleNodes.slice(1);
      // Center root
      rootNode.x = safeCx;
      rootNode.y = safeCy;
      // Dynamic radius to fit within safe bounds
      const maxNodeR = Math.max(...visibleNodes.map(d => getEffectiveRadius(d)));
      const baseRadius = isMobile ? 280 : 360;
      const maxCircleR = Math.min((safe.x1 - safe.x0) / 2, (safe.y1 - safe.y0) / 2) - maxNodeR - 20;
      const radius = Math.min(baseRadius, maxCircleR);
      // Position children in perfect circle
      childNodes.forEach((node, index) => {
        const angle = (index / childNodes.length) * 2 * Math.PI - Math.PI / 2;
        node.x = rootNode.x + Math.cos(angle) * radius;
        node.y = rootNode.y + Math.sin(angle) * radius;
        // Clamp to ensure within safe bounds
        const pad = getEffectiveRadius(node) + 20;
        node.x = Math.max(safe.x0 + pad, Math.min(safe.x1 - pad, node.x));
        node.y = Math.max(safe.y0 + pad, Math.min(safe.y1 - pad, node.y));
      });
    } else {
      // Show All mode: category-based clustering
      visibleNodes.forEach((node) => {
        const group = categoryGroups[node.category];
        if (group) {
          // Add some randomness for natural distribution within category
          const spreadFactor = nodeCount > 40 ? 200 : nodeCount > 20 ? 250 : 300;
          node.x = group.x + (Math.random() - 0.5) * spreadFactor;
          node.y = group.y + (Math.random() - 0.5) * spreadFactor;
        } else {
          node.x = safeCx + (Math.random() - 0.5) * 300;
          node.y = safeCy + (Math.random() - 0.5) * 300;
        }
        // Clamp to safe bounds
        const pad = getEffectiveRadius(node) + 20;
        node.x = Math.max(safe.x0 + pad, Math.min(safe.x1 - pad, node.x));
        node.y = Math.max(safe.y0 + pad, Math.min(safe.y1 - pad, node.y));
      });
    }
    // Create force simulation with optimized parameters
    const simulation = d3
      .forceSimulation(visibleNodes)
      .force("link", d3.forceLink(edgesData)
        .id((d) => d.id)
        .distance(isHierarchical ? (isMobile ? 220 : 300) : (isMobile ? 160 : 220))
        .strength(isHierarchical ? 0.4 : 0.2))
      .force("charge", d3.forceManyBody()
        .strength(isHierarchical ? (isMobile ? -1200 : -1800) : (isMobile ? -700 : -1000)))
      .force("collision", d3.forceCollide()
        .radius((d) => getEffectiveRadius(d) + (isHierarchical ? (isMobile ? 40 : 60) : (isMobile ? 40 : 50))) // Increased padding
        .strength(1.0)
        .iterations(3))
      .force("center", d3.forceCenter(safeCx, safeCy)
        .strength(0.03)) // This center force is good, it will create the cluster
      .force("x", d3.forceX()
        .x((d) => { // This x coordinate no longer matters when strength is 0
          if (isHierarchical) return safeCx;
          const group = categoryGroups[d.category];
          return group ? group.x : safeCx;
        })
        .strength(isHierarchical ? 0.05 : 0) // <-- CHANGED: Set strength to 0 for global view
      )
      .force("y", d3.forceY()
        .y((d) => { // This y coordinate no longer matters when strength is 0
          if (isHierarchical) return safeCy;
          const group = categoryGroups[d.category];
          return group ? group.y : safeCy;
        })
        .strength(isHierarchical ? 0.05 : 0) // <-- CHANGED: Set strength to 0 for global view
      )
      // We remove the boundary force from the main chain
      .alphaDecay(0.55)
      .alphaTarget(0)
      .velocityDecay(0.4);


    // Conditionally add the boundary force ONLY for the hierarchical view
    if (isHierarchical) {
      simulation.force(
        "boundary",
        forceBoundary(
          () => safe,
          d => getEffectiveRadius(d),
          24
        )
      );
    }

    simulationRef.current = simulation;
    // SVG Markers for arrows
    svg.append("defs").selectAll("marker")
      .data(["arrow-global"])
      .enter().append("marker")
      .attr("id", "arrow-global")
      .attr("viewBox", "0 -5 10 10")
      .attr("refX", 10)
      .attr("refY", 0)
      .attr("markerWidth", 6)
      .attr("markerHeight", 6)
      .attr("orient", "auto")
      .append("path")
      .attr("d", "M0,-5L10,0L0,5")
      .attr("fill", "#aaaaaa");
    // Links group
    const linkGroup = g.append("g").attr("class", "links");
    const link = linkGroup
      .selectAll("line")
      .data(edgesData)
      .enter()
      .append("line")
      .attr("stroke", "#aaaaaa")
      .attr("stroke-width", 1.5)
      .attr("stroke-opacity", (d) => {
        if (!selectedNode) return 0.5;
        return d.source.id === selectedNode.id || d.target.id === selectedNode.id ? 0.9 : 0.15;
      })
      .attr("marker-end", "url(#arrow-global)")
      .on("mouseover", function (event, d) {
        d3.select(this).attr("stroke-width", 1.5).attr("stroke-opacity", 0.9);
        const midX = (d.source.x + d.target.x) / 2;
        const midY = (d.source.y + d.target.y) / 2;
        const svgRect = svgRef.current?.getBoundingClientRect();
        if (svgRect) {
          setEdgeTooltip({
            label: d.label,
            x: svgRect.left + midX,
            y: svgRect.top + midY,
          });
        }
      })
      .on("mouseout", function () {
        d3.select(this)
          .attr("stroke-width", 1.5)
          .attr("stroke-opacity", (d) => {
            if (!selectedNode) return 0.5;
            return d.source.id === selectedNode.id || d.target.id === selectedNode.id ? 0.9 : 0.15;
          });
        setEdgeTooltip(null);
      });
    // Edge paths for labels
    const edgePaths = linkGroup
      .selectAll(".edgepath")
      .data(edgesData)
      .enter()
      .append("path")
      .attr("class", "edgepath")
      .attr("id", (d, i) => `edgepath-global-${i}`)
      .attr("stroke-width", 0)
      .attr("fill", "none");
    const edgeLabelTexts = linkGroup
      .selectAll(".edge-label-text")
      .data(edgesData)
      .enter()
      .append("text")
      .attr("class", "edge-label-text")
      .attr("font-size", isMobile ? "13px" : "13px")
      .attr("fill", "#ddd")
      .attr("text-anchor", "middle")
      .attr("dy", -8)
      .attr("font-weight", "500")
      .style("pointer-events", "none")
      .append("textPath")
      .attr("class", "edge-label")
      .attr("href", (d, i) => `#edgepath-global-${i}`)
      .attr("startOffset", "50%")
      .attr("href", (d, i) => `#edgepath-global-${i}`)
      .text((d) => d.label);
    // Nodes group
    const nodeGroup = g
      .append("g")
      .attr("class", "nodes")
      .selectAll("g")
      .data(visibleNodes)
      .enter()
      .append("g")
      .attr("class", "node-group")
      .style("cursor", (d) => hasChildren(d.id) && !showAllNodes ? "pointer" : "default")
      .on("click", (event, d) => {
        handleNodeClick(d);
        event.stopPropagation();
      })
      .on("mouseenter", (event, d) => {
        const nodeElement = event.currentTarget;
        const rect = nodeElement.getBoundingClientRect();
        const svgRect = svgRef.current?.getBoundingClientRect();
        if (svgRect) {
          setNodeTooltip({
            label: d.label,
            category: d.category,
            hasChildren: hasChildren(d.id),
            x: rect.left + rect.width / 2 - svgRect.left,
            y: rect.top - svgRect.top - 10,
          });
        }
      })
      .on("mouseleave", () => {
        setEdgeTooltip(null);
        setNodeTooltip(null);
      })
      .call(d3.drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended));
    // Node circles
    nodeGroup
      .append("circle")
      .attr("r", (d) => getEffectiveRadius(d, selectedNode && d.id === selectedNode.id))
      .attr("fill", (d) => {
        if (!selectedNode) return getNodeColor(d);
        if (d.id === selectedNode.id) return "#ff9900";
        const isConnected = allEdges.some(
          (e) => (e.from === selectedNode.id && e.to === d.id) ||
            (e.to === selectedNode.id && e.from === d.id)
        );
        return isConnected ? getNodeColor(d) : `${getNodeColor(d)}40`;
      })
      .attr("stroke", (d) => {
        if (selectedNode && d.id === selectedNode.id) return "#ffffff";
        if (hasChildren(d.id) && !showAllNodes) return "#00ff88";
        return "#333333";
      })
      .attr("stroke-width", (d) => {
        if (selectedNode && d.id === selectedNode.id) return 2;
        if (hasChildren(d.id) && !showAllNodes) return 2;
        return 0.5;
      })
      .attr("opacity", (d) => {
        if (!selectedNode) return 1;
        if (d.id === selectedNode.id) return 1;
        const isConnected = allEdges.some(
          (e) => (e.from === selectedNode.id && e.to === d.id) ||
            (e.to === selectedNode.id && e.from === d.id)
        );
        return isConnected ? 1 : 0.3;
      });
    // Node text labels
    const textGroups = nodeGroup
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", 0)
      .attr("class", "node-text")
      .attr("data-scale", 1)
      .style("pointer-events", "none")
      .style("user-select", "none");
    textGroups.each(function (d) {
      const text = d3.select(this);
      const radius = getEffectiveRadius(d, selectedNode && d.id === selectedNode.id);
      let fontSizeFactor;
      if (isHierarchical) {
        fontSizeFactor = isMobile ? 0.9 : 0.7; // Increased from 0.75
      } else if (nodeCount > 80) {
        fontSizeFactor = isMobile ? 0.8 : 0.7; // Increased from 0.6
      } else if (nodeCount > 40) {
        fontSizeFactor = isMobile ? 0.9 : 0.8; // Increased from 0.7
      } else if (nodeCount > 20) {
        fontSizeFactor = isMobile ? 0.95 : 0.85; // Increased from 0.75
      } else {
        fontSizeFactor = isMobile ? 1.0 : 0.9; // Increased from 0.8
      }
      const fontSize = Math.min(
        radius * fontSizeFactor,
        isHierarchical ? (isMobile ? 13 : 12) : // Increased from 14
          (nodeCount > 50 ? (isMobile ? 12 : 12) : (isMobile ? 13 : 13)) // Increased from 11/13
      );
      text.attr("font-size", `${fontSize}px`);
      text.attr("fill", "#ffffff");
      text.attr("font-weight", "500");
      const labelToDisplay = d.shortLabel || d.label;
if (radius < 20) {
        const words = labelToDisplay.split(/\s+/);
        const initials = words.length > 1 ? words.map((w) => w[0]).join("") : labelToDisplay.substring(0, 2);
        // Simple center for initials
        text.append("tspan").attr("x", 0).attr("y", 0).attr("dy", "0.35em").text(initials);
      } else {
        const maxCharsPerLine = Math.max(8, Math.floor(radius / 3.5));
        const lines = wrapNodeLabel(labelToDisplay, maxCharsPerLine);
        
        // 1. Define the height of a line (1.1em or 1.2em is standard)
        const lineHeight = 1.1;
        
        lines.forEach((line, i) => {
          // 2. Calculate the EXACT offset for this specific line relative to the center (0)
          // Formula: (Index - MiddleIndex) * LineHeight + CapHeightAdjustment
          const dyValue = (i - (lines.length - 1) / 2) * lineHeight + 0.35;
 
          text.append("tspan")
            .attr("x", 0)
            .attr("y", 0) // Force every line to reference the absolute vertical center
            .attr("dy", `${dyValue}em`) // Apply the calculated offset
            .text(line);
        });
      }
    });
    // Simulation tick with bounds checking
    simulation.on("tick", () => {
      // Update link positions
      link
        .attr("x1", (d) => d.source.x)
        .attr("y1", (d) => d.source.y)
        .attr("x2", (d) => {
          const dx = d.target.x - d.source.x;
          const dy = d.target.y - d.source.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const targetRadius = getEffectiveRadius(d.target);
          const shortenBy = targetRadius + 6;
          const ratio = (distance - shortenBy) / distance;
          return d.source.x + dx * ratio;
        })
        .attr("y2", (d) => {
          const dx = d.target.x - d.source.x;
          const dy = d.target.y - d.source.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const targetRadius = getEffectiveRadius(d.target);
          const shortenBy = targetRadius + 6;
          const ratio = (distance - shortenBy) / distance;
          return d.source.y + dy * ratio;
        });
      // Update edge paths for labels
      edgePaths.attr("d", (d) => {
        const sx = d.source.x;
        const sy = d.source.y;
        const tx = d.target.x;
        const ty = d.target.y;
        const dx = tx - sx;
        const dy = ty - sy;
        const dr = Math.sqrt(dx * dx + dy * dy);
        const targetRadius = getEffectiveRadius(d.target);
        const offset = targetRadius + 6;
        const normX = dx / dr;
        const normY = dy / dr;
        const ex = tx - normX * offset;
        const ey = ty - normY * offset;
        const angle = Math.atan2(dy, dx) * (180 / Math.PI);
        if (angle > 90 || angle < -90) {
          return `M${ex},${ey}L${sx},${sy}`;
        } else {
          return `M${sx},${sy}L${ex},${ey}`;
        }
      });
      // Update node positions
      nodeGroup.attr("transform", (d) => `translate(${d.x}, ${d.y})`);
      // Apply text scaling if zoomed
      if (currentZoomScale !== 1) {
        adjustTextScaling(d3, currentZoomScale);
      }
    });
    // Drag handlers
    function dragstarted(event) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }
    function dragged(event) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }
    function dragended(event) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }
    // Click on background to deselect
    svg.on("click", (event) => {
      const targetElement = event.target;
      if (!targetElement.closest(".node-group")) {
        setSelectedNode(null);
        setExpandedTerm(null);
        setViewMode("global")
      }
    });
    setTimeout(() => {
      const bounds = g.node().getBBox();
      // Get actual viewport dimensions
      const viewportWidth = container?.clientWidth || width;
      const viewportHeight = container?.clientHeight || height;
      // Calculate device pixel ratio for high-DPI screens
      const pixelRatio = window.devicePixelRatio || 1;
      const dpiAdjustment = pixelRatio > 1.5 ? 0.9 : 1.0;
      // Determine padding based on viewport size and node count
      let paddingX, paddingY;
      if (isMobile) {
        // REDUCED mobile padding for MORE zoom in
        paddingX = viewportWidth * 0.08; // Reduced from 0.1 to 0.08
        paddingY = viewportHeight * 0.10; // Reduced from 0.12 to 0.10
      } else {
        // Desktop padding unchanged
        if (viewportWidth > 1920) {
          paddingX = viewportWidth * 0.15;
          paddingY = viewportHeight * 0.15;
        } else if (viewportWidth > 1440) {
          paddingX = viewportWidth * 0.12;
          paddingY = viewportHeight * 0.13;
        } else {
          paddingX = viewportWidth * 0.1;
          paddingY = viewportHeight * 0.12;
        }
      }
      // Calculate scale to fit all nodes within viewport with padding
      const scaleX = (viewportWidth - paddingX * 2) / bounds.width;
      const scaleY = (viewportHeight - paddingY * 2) / bounds.height;
      // Use the smaller scale to ensure everything fits
      let scale = Math.min(scaleX, scaleY);
      // Apply maximum scale limits based on node count and viewport
      let maxScale;
      if (isHierarchical) {
        // Hierarchical mode: HIGHER max scale for mobile
        if (nodeCount <= 2) {
          maxScale = isMobile ? 0.8 : 0.8; // Increased from 0.6
        } else if (nodeCount <= 4) {
          maxScale = isMobile ? 0.9 : 0.95; // Increased from 0.7
        } else if (nodeCount <= 6) {
          maxScale = isMobile ? 1.0 : 1.05; // Increased from 0.75
        } else if (nodeCount <= 8) {
          maxScale = isMobile ? 1.1 : 1.15; // Increased from 0.8
        } else {
          maxScale = isMobile ? 1.2 : 1.2; // Increased from 0.85
        }
      } else if (showAllNodes || nodeCount > 20) {
        // Many nodes: HIGHER max scale for mobile
        maxScale = isMobile ? 0.85 : 0.85; // Increased from 0.7
      } else {
        // Medium size: HIGHER max scale for mobile
        maxScale = isMobile ? 1.0 : 1.1; // Increased from 0.8
      }
      // Apply max scale limit and DPI adjustment
      scale = Math.min(scale, maxScale) * dpiAdjustment;
      // Add extra safety margin for ultra-wide or non-standard aspect ratios
      const aspectRatio = viewportWidth / viewportHeight;
      if (aspectRatio > 2.0 || aspectRatio < 0.6) {
        scale *= 0.9;
      }
      // Center the graph properly
      const translateX = (viewportWidth - bounds.width * scale) / 2 - bounds.x * scale;
      const translateY = (viewportHeight - bounds.height * scale) / 2 - bounds.y * scale;
      // Apply zoom instantly (no transition)
      svg.call(zoom.transform, d3.zoomIdentity.translate(translateX, translateY).scale(scale));
      setCurrentZoomScale(scale);
      // Focus on selected node if exists
      if (selectedNode) {
        focusOnNode(d3, selectedNode.id);
      }
      // Stop simulation after stabilization
      setTimeout(() => {
        simulation.stop();
      }, showAllNodes || nodeCount > 20 ? 3500 : (isHierarchical ? 3000 : 2500));
    }, 150);
  };


  const adjustTextScaling = (d3, scale) => {
    if (!svgRef.current) return;
    const isMobile = isMobileView();
    let textScale;
    if (isMobile) {
      // MOBILE: Different behavior for zoom in vs zoom out
      if (scale >= 1) {
        // Zoomed in or at 1x: Scale text UP with zoom (proportional scaling)
        textScale = Math.min(scale * 1.2, 3.0); // Text gets bigger when zooming in
      } else {
        // Zoomed out (scale < 1): Use inverse to keep text readable
        const inverseScale = 1 / scale;
        textScale = Math.min(inverseScale * 0.9, 2.0);
      }
    } else {
      // DESKTOP: Keep original inverse scaling behavior
      const inverseScale = 1 / scale;
      textScale = Math.min(Math.max(inverseScale, 0.6), 2.0);
    }
    const container = graphContainerRef.current;
    const containerWidth = container?.clientWidth || 800;
    // const screenSizeFactor = Math.min(containerWidth / 1200, 1.3);
    const baseWidth = isMobile ? 400 : 1200; // Use 400px as the base for mobile
    const screenSizeFactor = Math.min(containerWidth / baseWidth, 1.3);
    const visibleNodeCount = getVisibleNodes().length;
    let densityFactor = 1.0;
    if (visibleNodeCount > 80) {
      densityFactor = 0.8;
    } else if (visibleNodeCount > 40) {
      densityFactor = 0.9;
    } else if (visibleNodeCount > 20) {
      densityFactor = 1.0;
    }
    // NEW: Only apply if scale has changed significantly to reduce fluctuations
    const prevScale = parseFloat(d3.selectAll(".node-text").attr("data-prev-scale") || 0);
    if (Math.abs(textScale - prevScale) < 0.05) return; // Threshold to avoid minor fluctuations
    d3.selectAll(".node-text").attr("data-prev-scale", textScale);
    // Apply text scaling to node labels
    d3.selectAll(".node-text").each(function () {
      const text = d3.select(this);
      const finalScale = textScale * screenSizeFactor * densityFactor;
      text.attr("transform", `scale(${finalScale})`);
    });
    // Edge labels also scale with mobile-aware logic
    const baseFontSize = isMobile ? 11 : 13;
    let edgeLabelScale;
    if (isMobile) {
      if (scale >= 1) {
        edgeLabelScale = Math.min(scale * 1.1, 2.5);
      } else {
        edgeLabelScale = Math.min((1 / scale) * 0.9, 1.8);
      }
    } else {
      edgeLabelScale = Math.min(Math.max(1 / scale, 0.7), 1.5);
    }
    const adjustedFontSize = baseFontSize * edgeLabelScale * screenSizeFactor * densityFactor;
    const baseDy = -8;
    const adjustedDy = baseDy * (isMobile && scale >= 1 ? scale * 0.8 : (1 / scale) * 0.8);
    d3.selectAll(".edge-label-text").each(function () {
      const text = d3.select(this);
      text.attr("font-size", `${adjustedFontSize}px`);
    });
    d3.selectAll(".edge-label").each(function () {
      const label = d3.select(this);
      label.attr("dy", adjustedDy);
      label.attr("font-weight", "500");
    });
  };

  useEffect(() => {
    if (!svgRef.current || !d3Ref.current) return;
    adjustTextScaling(d3Ref.current, currentZoomScale);
  }, [currentZoomScale]);

  useEffect(() => {
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (!graphInitialized || !svgRef.current || !d3Ref.current) return;
        setForceRender((prev) => prev + 1);
      }, 250);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [graphInitialized]);

  useEffect(() => {
    if (!graphInitialized && svgRef.current) {
      setGraphInitialized(true);
    }
  }, [graphInitialized]);

  const showOverlay = () => {
    setOverlayDismissed(false);
  };

  const dismissOverlay = () => {
    setOverlayDismissed(true);
    setViewMode("global");
    setSelectedNode(null);
    setExpandedTerm(null);
    setEdgeTooltip(null);
    setNodeTooltip(null);
    setForceRender((prev) => prev + 1);
  };

  const showHelp = () => {
    setHelpVisible(true);
  };

  const hideHelp = () => {
    setHelpVisible(false);
  };

  const togglePanelExpanded = () => {
    setIsPanelExpanded(!isPanelExpanded);
  };

  useEffect(() => {
    if (viewMode === "global") {
      setIsPanelExpanded(false);
    }
  }, [viewMode]);

  const getNodeConnections = (nodeId) => {
    if (!nodeId) return [];
    return allEdges
      .filter((edge) => edge.from === nodeId || edge.to === nodeId)
      .map((edge) => {
        const connectedNodeId = edge.from === nodeId ? edge.to : edge.from;
        const connectedNode = allNodes.find((n) => n.id === connectedNodeId);
        const direction = edge.from === nodeId ? "outgoing" : "incoming";
        return {
          relationshipType: edge.label,
          node: connectedNode,
          direction,
        };
      });
  };

  const handleSidebarNodeClick = (node) => {
    // Build the full path from root to this node
    const fullPath = getFullPathToNode(node.id);
    setNavigationStack(fullPath);
    if (hasChildren(node.id) && !showAllNodes) {
      // Node has children - show in hierarchy view (DESKTOP behavior)
      setViewMode("global");
      setSelectedNode(node);
      setExpandedTerm(node.id);
      setCurrentRootId(node.id);
    } else {
      // Leaf node - show in focus view (DESKTOP behavior)
      setViewMode("focus");
      setSelectedNode(node);
      setExpandedTerm(node.id);
      setIsPanelExpanded(false); // Don't auto-expand on desktop
      // Set parent as currentRootId for proper navigation back
      if (!showAllNodes && fullPath.length > 1) {
        setCurrentRootId(fullPath[fullPath.length - 2]);
      }
    }
    setEdgeTooltip(null);
    setNodeTooltip(null);
    setTimeout(() => {
      setForceRender((prev) => prev + 1);
    }, 10);
    // Scroll to the node in the list
    setTimeout(() => {
      const termElement = termItemRefs.current[node.id];
      if (termElement) {
        termElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }, 100);
  };

  const getBreadcrumbPath = () => {
    return navigationStack.map(nodeId => allNodes.find(n => n.id === nodeId)).filter(n => n);
  };

  return (
    <div className="flex w-full h-full bg-[#121212] text-white">
      {/* <div className="w-full md:w-[30%] h-full border-r border-[#333] overflow-y-auto flex flex-col scrollbar-thin scrollbar-thumb-[#444] scrollbar-track-[#222]"> */}
      <div className="hidden md:block w-full md:w-[30%] h-full border-r border-[#333] overflow-y-auto flex flex-col scrollbar-thin scrollbar-thumb-[#444] scrollbar-track-[#222]">
        {/* Search Bar */}
        {/* <div className="p-4 border-b border-[#333]"> */}
        <div className="sticky top-0 z-10 bg-[#121212] p-4 border-b border-[#333]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search AI terms..."
              className="w-full px-10 py-2 rounded-md bg-[#222] text-white border border-[#555] focus:outline-none focus:border-[#777]"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white hover:bg-[#333] rounded transition-colors"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {/* Search Mode Banner desktop */}
          {searchTerm && (
            <div className="mt-2 flex items-center justify-between">
              <div className="text-xs text-gray-400">
                Found {filteredNodes.length} result{filteredNodes.length !== 1 ? 's' : ''} across all nodes
              </div>
              {filteredNodes.length > 0 && (
                <div className="text-xs text-blue-400 flex items-center">
                  <Eye className="w-3 h-3 mr-1" />
                  Click to view
                </div>
              )}
            </div>
          )}
        </div>
        {/* Search Mode Banner */}
        {searchTerm && (
          <div className="px-4 py-2 bg-[#1a1a1a] border-b border-[#333] flex items-center justify-between">
            <div className="flex items-center text-xs text-blue-400">
              <Search className="w-3 h-3 mr-1" />
              <span>Searching globally across all {allNodes.length} nodes</span>
            </div>
          </div>
        )}
        {/* Categorized Term List */}
        <div className="flex-grow overflow-y-auto p-2" ref={termListRef}>
          {searchTerm && filteredNodes.length === 0 ? (
            <div className="text-center text-gray-400 mt-8">
              <Search className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No results found for "{searchTerm}"</p>
            </div>
          ) : (
            groupedNodes.map((group) => (
              <div key={group.category} className="mb-4">
                <div className="flex items-center mb-2">
                  <div
                    className="w-4 h-4 rounded-full mr-2"
                    style={{
                      backgroundColor: categoryColors[group.category] || "#444",
                    }}
                  ></div>
                  <h3 className="text-lg font-semibold text-gray-300">{group.category}</h3>
                  <span className="ml-2 text-xs text-gray-500">({group.nodes.length})</span>
                </div>
                <div className="ml-6 space-y-1">
                  {group.nodes.map((node) => (
                    <div
                      key={node.id}
                      ref={(el) => (termItemRefs.current[node.id] = el)}
                      className={`rounded-md cursor-pointer transition-all duration-200 ${selectedNode?.id === node.id
                        ? "bg-[#333] border-l-4 border-[#ff9900]"
                        : searchTerm
                          ? "bg-[#2a2a2a] hover:bg-[#333]"
                          : "hover:bg-[#333]"
                        }`}
                      onClick={() => handleNodeClick(node)}
                    >
                      <div className="px-3 py-2 flex items-center justify-between">
                        <div className="flex-1">
                          <div className="font-medium flex items-center">
                            {node.label}
                            {hasChildren(node.id) && !showAllNodes && !searchTerm && (
                              <ChevronRight className="w-4 h-4 ml-1 text-green-400" />
                            )}
                            {searchTerm && (
                              <span className="ml-2 flex items-center text-xs text-blue-400">
                                <Eye className="w-3 h-3 mr-1" />
                                View
                              </span>
                            )}
                          </div>
                          {expandedTerm !== node.id && (
                            <div className="text-xs text-gray-400">
                              {node.definition.substring(0, 60)}...
                            </div>
                          )}
                        </div>
                      </div>
                      {expandedTerm === node.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          className="px-3 pb-3 text-sm"
                        >
                          <div className="mb-2 text-gray-300 whitespace-pre-wrap break-words">
                            {node.definition}
                          </div>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <div className="w-full h-full relative" ref={graphContainerRef}>
        <svg ref={svgRef} className="w-full h-full"></svg>
        {/* Mobile Search Bar with Simple Suggestions */}
        <div className="md:hidden absolute top-4 left-3 right-3 z-10 flex items-center gap-1.5">
          {/* Info button */}
          <button
            onClick={showOverlay}
            className="w-8 h-8 bg-[#333] hover:bg-[#444] text-white rounded-full flex items-center justify-center shadow-lg flex-shrink-0"
            title="Show introduction"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </button>
          {/* Help button */}
          <button
            onClick={showHelp}
            className="w-8 h-8 bg-[#333] hover:bg-[#444] text-white rounded-full flex items-center justify-center shadow-lg flex-shrink-0"
            title="Show help"
          >
            <span className="font-bold text-sm">?</span>
          </button>
          {/* Mobile Search box with dropdown suggestions */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none z-10" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowMobileSuggestions(e.target.value.length > 0); // Show dropdown when typing
              }}
              onFocus={() => {
                if (searchTerm) setShowMobileSuggestions(true);
              }}
              placeholder="Search..."
              className="w-full pl-8 pr-8 py-1.5 rounded-md bg-[#222] text-white border border-[#555] focus:outline-none focus:border-[#777] text-sm relative"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm("");
                  setShowMobileSuggestions(false);
                }}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white hover:bg-[#333] rounded transition-colors z-10"
                title="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            {/* Backdrop to close dropdown when clicking outside */}
            {showMobileSuggestions && searchTerm && filteredNodes.length > 0 && (
              <div
                className="md:hidden fixed inset-0 z-40"
                onClick={() => setShowMobileSuggestions(false)}
              />
            )}
            {/* Mobile-only dropdown */}
            {showMobileSuggestions && searchTerm && filteredNodes.length > 0 && (
              <div className="md:hidden absolute top-full left-0 right-0 mt-1 bg-[#222] border border-[#555] rounded-md shadow-xl z-50 max-h-48 overflow-y-auto">
                {filteredNodes.slice(0, 4).map((node) => (
                  <div
                    key={node.id}
                    className="px-3 py-2 hover:bg-[#333] cursor-pointer border-b border-[#444] last:border-b-0 flex items-center justify-between"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent backdrop click
                      handleNodeClick(node);
                      // setSearchTerm(""); // Clear search
                      setShowMobileSuggestions(false); // Close dropdown
                    }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-white truncate">{node.label}</div>
                      <div className="text-xs text-gray-400">{node.category}</div>
                    </div>
                    {hasChildren(node.id) && !showAllNodes && (
                      <ChevronRight className="w-3.5 h-3.5 text-green-400 flex-shrink-0 ml-2" />
                    )}
                  </div>
                ))}
                {filteredNodes.length > 4 && (
                  <div className="px-3 py-2 text-xs text-center text-gray-400 bg-[#1a1a1a]">
                    +{filteredNodes.length - 4} more results
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Show All Toggle Button */}
          <button
            onClick={toggleShowAll}
            className={`w-8 h-8 rounded-md shadow-lg flex items-center justify-center transition-colors flex-shrink-0 ${showAllNodes
              ? 'bg-[#ff9900] hover:bg-[#ff7700] text-white'
              : 'bg-[#333] hover:bg-[#444] text-white'
              }`}
            title={showAllNodes ? "Show hierarchical view" : "Show all nodes"}
          >
            <Layers className="w-4 h-4" />
          </button>
        </div>
        {/* Mobile Categories Button - Below search bar */}
        <button
          onClick={() => setShowMobileCategories(true)}
          className="md:hidden absolute top-16 right-3 z-10 w-8 h-8 bg-[#333] hover:bg-[#444] text-white rounded-md flex items-center justify-center shadow-lg"
          title="Show categories"
        >
          <Palette className="w-4 h-4" />
        </button>
        {/* Mobile Categories Button with Popover */}
        <div className="md:hidden absolute top-16 right-3 z-10">
          <button
            onClick={() => setShowMobileCategories(!showMobileCategories)}
            className={`w-8 h-8 rounded-md flex items-center justify-center shadow-lg transition-colors ${showMobileCategories ? 'bg-[#ff9900] text-white' : 'bg-[#333] hover:bg-[#444] text-white'
              }`}
            title="Show categories"
          >
            <Palette className="w-4 h-4" />
          </button>
          {/* Popover */}
          {showMobileCategories && (
            <>
              {/* Backdrop to close popover */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setShowMobileCategories(false)}
              />
              {/* Popover content */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full right-0 mt-2 w-64 bg-[#222] border border-[#555] rounded-lg shadow-2xl z-50 max-h-[60vh] overflow-y-auto"
              >
                {/* Header */}
                <div className="sticky top-0 bg-[#222] border-b border-[#444] px-3 py-2 flex items-center justify-between z-10">
                  <h3 className="text-sm font-semibold">Categories</h3>
                  <button
                    onClick={() => setShowMobileCategories(false)}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {/* Categories list */}
                <div className="p-2">
                  {Object.entries(categoryColors)
                    .filter(([category]) => category !== "Root")
                    .map(([category, color]) => (
                      <div
                        key={category}
                        className="flex items-center px-2 py-1.5 rounded hover:bg-[#2a2a2a] transition-colors"
                      >
                        <div
                          className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
                          style={{ backgroundColor: color }}
                        ></div>
                        <span className="text-xs">{category}</span>
                      </div>
                    ))}
                </div>
              </motion.div>
            </>
          )}
        </div>
        {((navigationStack.length > 1) || (viewMode === 'focus' && selectedNode)) && (
          <div className="absolute top-14 md:top-20 left-3 md:left-4 right-14 md:right-auto z-20">
            {/* ---------- MOBILE (compact) ---------- */}
            {/* <div className="md:hidden bg-[#222] rounded-md px-3 py-2 shadow-lg flex items-center gap-2">
              <button
                onClick={navigateBack}
                className="flex text-gray-400 hover:text-white transition-colors flex-shrink-0"
                title="Go back"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                <span className="text-sm">back</span>
              </button>
              <div className="flex items-center gap-1 min-w-0 flex-1">
                <button
                  onClick={resetToRoot}
                  className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
                  title="Go to root"
                >
                  <Home className="w-3.5 h-3.5" />
                </button>
                {navigationStack.length > 2 && (
                  <>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    <span className="text-xs text-gray-500">…</span>
                  </>
                )}
                <ChevronRight className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                <span className="text-sm text-white font-semibold truncate">
                  {getBreadcrumbPath()[getBreadcrumbPath().length - 1]?.label}
                </span>
              </div>
            </div> */}
            {/* mobile not scrollable */}
            <div className="md:hidden bg-[#222] rounded-md px-3 py-2 shadow-lg flex items-center gap-2">
              <button
                onClick={navigateBack}
                className="flex text-gray-400 hover:text-white transition-colors flex-shrink-0"
                title="Go back"
              >
                <ChevronRight className="w-4 h-4 rotate-180" />
                <span className="text-sm">back</span>
              </button>
              <div className="flex items-center gap-1 min-w-0 flex-1">
                <button
                  onClick={resetToRoot}
                  className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
                  title="Go to root"
                >
                  <Home className="w-3.5 h-3.5" />
                </button>
                {navigationStack.length > 2 && (
                  <>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    <span className="text-xs text-gray-500">…</span>
                  </>
                )}
                <ChevronRight className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                <span className="text-sm text-white font-semibold truncate">
                  {getBreadcrumbPath()[getBreadcrumbPath().length - 1]?.label}
                </span>
              </div>
            </div>
            {/* ---------- DESKTOP (full + hide/show) ---------- */}
            <div className="hidden md:flex items-center gap-2">
              {/* The whole bar – slides in/out */}
              <motion.div
                className="bg-[#222] rounded-md px-4 py-2 shadow-lg max-w-3xl flex items-center gap-2"
                initial={false}
                animate={{
                  width: breadcrumbCollapsed ? 0 : 'auto',
                  opacity: breadcrumbCollapsed ? 0 : 1,
                  paddingLeft: breadcrumbCollapsed ? 0 : 16,
                  paddingRight: breadcrumbCollapsed ? 0 : 16,
                }}
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                style={{
                  overflowX: breadcrumbCollapsed ? 'hidden' : 'auto',
                  overflowY: 'hidden'
                }}
              >
                <button
                  onClick={resetToRoot}
                  className="text-gray-400 hover:text-white transition-colors flex-shrink-0"
                  title="Go to root"
                >
                  <Home className="w-4 h-4" />
                </button>
                {getBreadcrumbPath().map((node, index, arr) => (
                  <React.Fragment key={node.id}>
                    {index > 0 && <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                    <button
                      onClick={() => navigateToNode(node.id)}
                      className={`text-sm whitespace-nowrap transition-colors flex-shrink-0 px-1 py-0.5 rounded hover:bg-[#333] ${index === arr.length - 1 ? 'text-white font-semibold' : 'text-gray-400 hover:text-white'
                        }`}
                      title={node.label}
                    >
                      {node.label}
                    </button>
                  </React.Fragment>
                ))}
              </motion.div>
              {/* Toggle button – always visible */}
              <button
                onClick={() => setBreadcrumbCollapsed(!breadcrumbCollapsed)}
                className="px-2 py-1 rounded-md bg-[#333] hover:bg-[#444] text-white shadow-lg transition-colors"
                title={breadcrumbCollapsed ? 'Show breadcrumbs' : 'Hide breadcrumbs'}
              >
                <ChevronLeft
                  className={`w-4 h-4 transition-transform duration-200 ${breadcrumbCollapsed ? 'rotate-180' : ''
                    }`}
                />
              </button>
            </div>
          </div>
        )}
        {/* Node Tooltip */}
        {nodeTooltip && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute z-30 bg-[#222] border border-[#555] rounded-md px-3 py-2 text-sm shadow-lg pointer-events-none"
            style={{
              left: nodeTooltip.x,
              top: nodeTooltip.y,
              transform: 'translateX(-50%)',
            }}
          >
            <div className="font-semibold text-white whitespace-nowrap flex items-center gap-2">
              {nodeTooltip.label}
              {searchTerm && (
                <span className="text-xs text-blue-400">(click to view all connections)</span>
              )}
              {nodeTooltip.hasChildren && !showAllNodes && !searchTerm && (
                <span className="text-xs text-green-400">(click to expand)</span>
              )}
            </div>
            {nodeTooltip.category && (
              <div className="text-xs text-gray-400 mt-0.5">{nodeTooltip.category}</div>
            )}
          </motion.div>
        )}
        {/* Definition & Example Panel */}
        {selectedNode && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-0 left-0 right-0 bg-[#222222ee] backdrop-blur-sm border-t md:border-t border-[#ff9900] md:border-[#444] z-20 md:z-10 shadow-2xl md:shadow-none flex flex-col"
            style={{
              maxHeight: isPanelExpanded ? (window.innerWidth < 768 ? '65%' : '50%') : (window.innerWidth < 768 ? '40%' : '25%'),
              minHeight: window.innerWidth < 768 ? '200px' : 'auto'
            }}
          >
            <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="container mx-auto p-3 md:p-4 pb-10 md:pb-4">
                <div className="flex items-center justify-between mb-2 md:mb-2 sticky top-0 bg-[#222222ee] backdrop-blur-sm pb-2 z-10">
                  <div className="flex items-center flex-wrap gap-1">
                    <div
                      className="w-4 h-4 rounded-full mr-2 flex-shrink-0"
                      style={{ backgroundColor: "#ff9900" }} //updating the circle color to orange i.e selected focused color
                    ></div>
                    <h3 className="text-base md:text-lg font-semibold text-white">{selectedNode.label}</h3>
                    <span className="ml-2 text-xs text-gray-400">{selectedNode.category}</span>
                    {viewMode === 'global' && hasChildren(selectedNode.id) && (
                      <span className="ml-2 text-xs text-green-400 hidden md:inline">(has sub-topics)</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={togglePanelExpanded}
                      className="text-xs md:text-sm text-gray-400 hover:text-white transition-colors focus:outline-none whitespace-nowrap px-2 py-1 md:px-0 md:py-0 bg-[#333] md:bg-transparent rounded md:rounded-none"
                    >
                      {isPanelExpanded ? (window.innerWidth < 768 ? "Less" : "Show Less") : (window.innerWidth < 768 ? "More" : "Show More...")}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedNode(null);
                        setExpandedTerm(null);
                        // setViewMode("global")
                      }}
                      className="text-gray-400 hover:text-white transition-colors focus:outline-none p-1 md:p-0 bg-[#333] md:bg-transparent rounded md:rounded-none"
                      title="Close"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="mb-3 md:mb-0">
                  <h4 className="text-xs uppercase font-semibold text-gray-400 mb-1">Definition</h4>
                  <p className="text-sm text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
                    {selectedNode.definition}
                  </p>
                </div>
                {isPanelExpanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    transition={{ duration: 0.3 }}
                    className="mt-4 space-y-4"
                  >
                    <div>
                      <h4 className="text-xs uppercase font-semibold text-gray-400 mb-1">Example</h4>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap break-words">
                        {selectedNode.mars_cpg_example}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-xs uppercase font-semibold text-gray-400 mb-1">
                        Mars Wrigley Example
                      </h4>
                      <p className="text-sm text-gray-300 whitespace-pre-wrap break-words">
                        {selectedNode.mars_wrigley_example}
                      </p>
                    </div>
                    <div className="pb-4 md:pb-0">
                      <h4 className="text-xs uppercase font-semibold text-gray-400 mb-2">Connections</h4>
                      <div className="flex flex-wrap gap-2 md:gap-1">
                        {getConnectedNodes(selectedNode.id).map(conn => (
                          <span
                            key={conn.id}
                            className="inline-block px-3 md:px-2 py-1.5 md:py-1 text-xs rounded-full bg-[#2a2a2a] hover:bg-[#444] cursor-pointer transition-colors border md:border-0 border-[#444]"
                            onClick={(e) => {
                              e.stopPropagation();
                              // Update navigation stack before selecting
                              const nodeIndex = navigationStack.indexOf(conn.id);
                              if (nodeIndex === -1) {
                                // Not in stack - add it
                                setNavigationStack([...navigationStack, conn.id]);
                              } else {
                                // Already in stack - trim to that point
                                setNavigationStack(navigationStack.slice(0, nodeIndex + 1));
                              }
                              // Now select the node
                              handleNodeSelect(conn);
                            }}
                          >
                            {conn.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
            {/* Scroll indicator for mobile */}
            {isPanelExpanded && (
              <div className="md:hidden absolute bottom-2 left-1/2 -translate-x-1/2 pointer-events-none">
                <div className="text-gray-500 text-xs animate-bounce">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            )}
          </motion.div>
        )}
        {/* Legend - Hidden on mobile */}
        <div className="hidden md:block absolute top-16 right-4 bg-[#111111dd] rounded-md p-3 shadow-lg max-h-80 overflow-y-auto">
          <h3 className="text-sm font-semibold mb-2">Categories</h3>
          <div className="space-y-1.5">
            {Object.entries(categoryColors)
              .filter(([category]) => category !== "Root")
              .map(([category, color]) => (
                <div key={category} className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: color }}
                  ></div>
                  <span className="text-xs">{category}</span>
                </div>
              ))}
          </div>
        </div>
        {/* Info button */}
        {/* Desktop-only Info button */}
        <button
          onClick={showOverlay}
          className="hidden md:flex absolute top-4 left-4 z-10 w-10 h-10 bg-[#333] hover:bg-[#444] text-white rounded-full items-center justify-center shadow-lg"
          title="Show introduction"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        </button>
        {/* Desktop-only Show All Toggle Button */}
        <button
          onClick={toggleShowAll}
          className={`hidden md:flex absolute top-4 right-4 z-10 px-4 py-2 rounded-md shadow-lg items-center gap-2 transition-colors ${showAllNodes
            ? 'bg-[#ff9900] hover:bg-[#ff7700] text-white'
            : 'bg-[#333] hover:bg-[#444] text-white'
            }`}
          title={showAllNodes ? "Show hierarchical view" : "Show all nodes"}
        >
          <Layers className="w-4 h-4" />
          <span className="text-sm">{showAllNodes ? "Focus Mode" : "Full graph view"}</span>
        </button>
        {/* Desktop-only Help button */}
        <button
          onClick={showHelp}
          className="hidden md:flex absolute top-4 left-[4rem] z-10 w-10 h-10 bg-[#333] hover:bg-[#444] text-white rounded-full items-center justify-center shadow-lg"
          title="Show help"
        >
          <span className="font-bold text-lg">?</span>
        </button>
        {/* Initial Instructions Overlay */}
        {!overlayDismissed && (
          <div
            className="absolute inset-0 flex items-center justify-center cursor-pointer z-[60] bg-[#00000080]"
            onClick={dismissOverlay}
          >
            <div
              className="text-center p-6 bg-[#22222288] rounded-lg backdrop-blur-sm max-w-md mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold mb-4">AI Learning Mind Map</h2>
              <p className="text-gray-300 mb-2">
                Explore AI concepts hierarchically or view the full network.
              </p>
              <p className="text-gray-400 text-sm mb-2">
                Click nodes with a <span className="text-green-400">green border</span> to drill deeper.
                Use breadcrumbs to navigate back.
              </p>
              <p className="text-gray-400 text-sm mb-4">
                <span className="text-blue-400">Search globally</span> to find any concept and view all its connections.
              </p>
              <button
                className="px-4 py-2 bg-[#333] hover:bg-[#444] rounded-md text-white"
                onClick={dismissOverlay}
              >
                Explore Now
              </button>
            </div>
          </div>
        )}
        {/* Help Overlay */}
        {helpVisible && (
          <div
            className="absolute inset-0 z-[70] bg-[#00000099] flex items-center justify-center cursor-pointer p-4"
            onClick={hideHelp}
          >
            <div
              className="bg-[#222] bg-opacity-95 rounded-lg max-w-lg p-6 text-sm backdrop-blur-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold mb-4 text-center">How to use the AI Mind Map</h3>
              <div className="space-y-3 text-gray-300 text-left">
                <div className="flex items-start gap-2">
                  <Search className="w-5 h-5 mt-0.5 text-white flex-shrink-0" />
                  <span>Search <b>globally</b> across all {allNodes.length} nodes by name, category, or definition. Click results to view all connections.</span>
                </div>
                <div className="flex items-start gap-2">
                  <MousePointerClick className="w-5 h-5 mt-0.5 text-white flex-shrink-0" />
                  <span>Click nodes with <span className="text-green-400">green borders</span> to drill into subcategories. Use breadcrumbs to navigate back.</span>
                </div>
                <div className="flex items-start gap-2">
                  <Layers className="w-5 h-5 mt-0.5 text-white flex-shrink-0" />
                  <span>Toggle "Full graph view" to view the complete network view.</span>
                </div>
                <div className="flex items-start gap-2">
                  <Hand className="w-5 h-5 mt-0.5 text-white flex-shrink-0" />
                  <span>Drag nodes to reposition them - they'll stay where you place them.</span>
                </div>
                <div className="flex items-start gap-2">
                  <MousePointer className="w-5 h-5 mt-0.5 text-white flex-shrink-0" />
                  <span>Drag background to pan; use mouse-wheel or <Plus className="inline w-4 h-4" />/<Minus className="inline w-4 h-4" /> buttons to zoom.</span>
                </div>
                <div className="flex items-start gap-2">
                  <Home className="w-5 h-5 mt-0.5 text-white flex-shrink-0" />
                  <span>Click the home icon in breadcrumbs to return to the root.</span>
                </div>
              </div>
              <button
                onClick={hideHelp}
                className="mt-5 block mx-auto px-4 py-2 bg-[#333] hover:bg-[#444] rounded-md text-white"
              >
                Got it
              </button>
            </div>
          </div>
        )}
        {/* Zoom controls - Repositioned for mobile & desktop view */}
        <div className="absolute bottom-[8.5rem] right-4 md:bottom-6 md:right-6 flex flex-col gap-2">
          <button
            className="w-9 h-9 md:w-10 md:h-10 bg-[#333] rounded-full flex items-center justify-center text-lg md:text-xl hover:bg-[#444] transition-colors disabled:opacity-50 shadow-lg"
            onClick={handleZoomIn}
            disabled={zoomInProgress}
            title="Zoom in"
          >
            +
          </button>
          <button
            className="w-9 h-9 md:w-10 md:h-10 bg-[#333] rounded-full flex items-center justify-center text-lg md:text-xl hover:bg-[#444] transition-colors disabled:opacity-50 shadow-lg"
            onClick={handleZoomOut}
            disabled={zoomInProgress}
            title="Zoom out"
          >
            −
          </button>
        </div>
      </div>
    </div>
  );
}